import { useMutation } from '@apollo/client'
import Popover from 'components/popover/popover'
// Components
import EditProfile from 'components/profile/edit-profile'
import { AuthError, ProfileButtons_s } from 'components/profile/profile-styles'
import UpdatePassword from 'components/profile/update-password'
import { WithTip } from 'components/tooltip/with-tip'
import { HeaderIcon_s } from 'components/wrapper/header/header-styles'
import { local } from 'data/lib/local-json'
import notificationService from "data/notification"
// Data
import profileService, { ProfileData } from 'data/profile'
// Icons
import { ProfileIcon } from 'images/icons/header/'
import _ from "lodash"
import React, { FC, useEffect, useRef, useState } from 'react'
import Isvg from 'react-inlinesvg'
// Styles
import { ButtonWithPress_s, Button_s } from 'styles/app-styles'

export type ProfileDataWithToken = ProfileData & { token: string};

const ProfilePopover: FC = () => {
  const profilePopoverIcon                        = useRef<any>();
  const [showPopover, togglePopover]              = useState<boolean>(false);
  const [editProfileMode, toggleProfileMode]      = useState<boolean>(true);
  const [profileData, updateData]                 = useState<ProfileDataWithToken>();
  const [error, setError]                         = useState<string | JSX.Element>();
  const [updateProfile, { error: _error }] = useMutation(profileService.updateProfile, {
    errorPolicy: 'none',
    onCompleted: () => onFinish(),
    onError: (e => {
      if (e.message === 'invalid current password') profileService.updateData('error', 'Current password is incorrect.')
      else if (e.message === 'invalid updated password') profileService.updateData('error', 'Updated password does not meet requirements.')
      else if (e.message === 'invalid_parameters') profileService.updateData('error', 'Missing required fields')
      else {
        let message = e.message
        if (message.includes('\n')) {
          const messages = message.split('\n')
          const $message = <div>{messages[0]}<ul>{_.map(messages.slice(1), s => <li>{s}</li>)}</ul></div>
          setError($message)
        } else {
          setError(message)
        }
      }
    })
  });

  useEffect(() => {
    profileService.stream().subscribe(obj => {
      if (obj.data){
        let data = obj['data'];
        updateData({...data, token: local('auth.token')});
      }
    });
  }, [error]);

  function closePopover():void {
    setTimeout(() => {
      togglePopover(false);
      toggleProfileMode(true);
    }, 500)
  }

  function onFinish():void {
    const message = editProfileMode ? 'Profile updated successfully' : 'Password updated successfully'
    notificationService.notify({ type: "info", message, dismiss: 5000 });
    closePopover();
  }

  function onSave():void {
    if (!profileData) return
    if (editProfileMode){
      if (profileData.firstName === "" || profileData.lastName === ""){
        setError('Missing required fields');
      } else {
        const name = `${profileData.firstName} ${profileData.lastName}`
        updateProfile({ variables: { token: profileData.token, name }});
      }
    } else {
      if (profileData.confirmNewPw === "" || profileData.currentPassword === "" || profileData.updatedPassword === "") {
        setError('Missing required fields');
      } else if (profileData.confirmNewPw !== profileData.updatedPassword) {
        setError('Updated passwords do not match, please try again.');
      } else {
        updateProfile({ variables: _.pick(profileData, 'currentPassword', 'token', 'updatedPassword')})
      }
    }
  }

  function changeMode():void {
    toggleProfileMode(!editProfileMode);
    profileService.updateData('error', null);
  }

  const ProfileOverlay = (
    <Popover
      position="top right"
      show={showPopover}
      target='profilePopoverIcon'
      title={editProfileMode ? 'Edit Profile' : 'Update Password'}
      toggleShow={togglePopover}
      width={350}
    >
      { error && <AuthError>{error}</AuthError> }
      { editProfileMode ? <EditProfile /> : <UpdatePassword profileData={profileData} /> }
      <ProfileButtons_s>
        <Button_s onClick={()=>changeMode()}>{editProfileMode ? 'Update Password' : 'Edit Profile'}</Button_s>
        <Button_s onClick={closePopover}>Cancel</Button_s>
        <ButtonWithPress_s onClick={onSave} primary>Save</ButtonWithPress_s>
      </ProfileButtons_s>
    </Popover>
  );

  return (
    <HeaderIcon_s>
      <WithTip position="bottom">
        <span>Profile</span>
        <a onClick={()=>togglePopover(!showPopover)} ref={profilePopoverIcon}>
          <Isvg id="profilePopoverIcon" src={ProfileIcon} />
        </a>
      </WithTip>
      { ProfileOverlay }
    </HeaderIcon_s>
  )
}

export default ProfilePopover